import type { FC } from "react";
import { useMemo, useState } from "react";

import { Typography } from "@ui/components/Typography";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorMessage } from "@ui/utils/backendError";

import { useConfig } from "@jti/app-context/useConfig";
import { Loader } from "@jti/core-components/components/Loader";
import { toast } from "@jti/core-components/components/Toaster";
import { useProviderCodes, useRequestCoupon } from "@jti/rest-api";

import { Promotion } from "./Promotion/Promotion";

import styles from "./PromotionCodes.module.scss";

export interface IPromotion {
  code: string;
  imageUrl: string;
}

export interface PromotionCodesProps {
  title: string;
  description: string;
  successMessage: string;
  errorMessage: string;
  promotions: IPromotion[];
}

export const PromotionCodes: FC<PromotionCodesProps> = ({
  title,
  description,
  successMessage,
  errorMessage,
  promotions,
}) => {
  const [activePromotionCode, setActivePromotionCode] = useState<string | null>(null);
  const { ISO_COUNTRY_CODE } = useConfig();
  const { __ } = useTranslations();
  const { providerCodes, isProviderCodesLoading, isProviderCodesError, providerCodesError } =
    useProviderCodes(ISO_COUNTRY_CODE);
  const { requestCoupon, isRequestCouponLoading } = useRequestCoupon();

  const handlePromotionClick = async (code: string) => {
    setActivePromotionCode(code);
    try {
      await requestCoupon(code);
      toast.success(successMessage);
    } catch (error) {
      toast.error(getBackendErrorMessage(error) || errorMessage);
    } finally {
      setActivePromotionCode(null);
    }
  };

  const filteredPromotions = useMemo(
    () => promotions.filter((promotion) => providerCodes?.includes(promotion.code)),
    [promotions, providerCodes]
  );

  return (
    <div className={styles.container}>
      {(isProviderCodesLoading || isRequestCouponLoading) && (
        <div className={styles.loadingOverlay}>
          <Loader data-testid="loading-spinner" />
        </div>
      )}
      <div className={styles.textContainer}>
        <Typography variant="h2">{title}</Typography>
        <div className={styles.description}>{description}</div>
      </div>
      {isProviderCodesError && <Typography variant="body1">{providerCodesError?.message}</Typography>}
      {!isProviderCodesError && !isProviderCodesLoading && filteredPromotions.length === 0 && (
        <Typography variant="body1">{__("libs.ui.promotion.no_promotions")}</Typography>
      )}
      {filteredPromotions.length > 0 && (
        <div className={styles.promotionsContainer}>
          {filteredPromotions.map((promotion) => (
            <Promotion
              key={promotion.code}
              code={promotion.code}
              imageUrl={promotion.imageUrl}
              onClick={() => handlePromotionClick(promotion.code)}
              isActive={activePromotionCode === promotion.code}
            />
          ))}
        </div>
      )}
    </div>
  );
};
